import axios from "axios";

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "http://localhost:8000",
});

// Automatically attach JWT token to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// A 401 from anywhere other than the login endpoints means the token is
// missing/expired — without this, every request on an already-open tab just
// fails silently instead of sending the user back to log in again.
//
// A page can fire several requests in parallel, all failing at once when the
// token dies — this flag makes sure only the first 401 reads/clears session
// state and redirects. It's reset when a 401 is "handled" without actually
// navigating away (we're already on the destination page), so a later, genuinely
// new 401 in the same tab doesn't get silently swallowed forever.
let handling401 = false;

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const isLoginRequest = /\/(admin|employee)\/login$/.test(error.config?.url || "");

    if (status === 401 && !isLoginRequest && !handling401) {
      handling401 = true;
      const isImpersonating = !!localStorage.getItem("hrms_admin_token");

      if (isImpersonating) {
        // The impersonated employee's token died (e.g. deactivated mid-session) —
        // fall back to the still-valid admin session instead of a full logout,
        // mirroring VerifyContext's leaveImpersonation(). If the admin's own token
        // has *also* died, the next request 401s again with isImpersonating now
        // false, correctly falling through to the full logout below.
        const adminToken = localStorage.getItem("hrms_admin_token")!;
        localStorage.setItem("token", adminToken);
        localStorage.setItem("authUserType", "admin");
        localStorage.removeItem("hrms_admin_token");
        localStorage.removeItem("hrms_impersonated_employee");

        if (window.location.pathname !== "/") {
          window.location.href = "/";
        } else {
          handling401 = false;
        }
      } else {
        const authUserType = localStorage.getItem("authUserType");
        localStorage.removeItem("token");
        localStorage.removeItem("authUserType");
        localStorage.removeItem("hrms_admin_token");
        localStorage.removeItem("hrms_impersonated_employee");

        const loginPath = authUserType === "admin" ? "/admin/login" : "/employee/login";
        if (window.location.pathname !== loginPath) {
          window.location.href = loginPath;
        } else {
          handling401 = false;
        }
      }
    }

    return Promise.reject(error);
  }
);

export default api;
